import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import './ADSCycle.css';
import axios from 'axios';
import * as XLSX from 'xlsx';
import DataTable, { ActionMenu, DataTableColumn } from './DataTable';
import { Icons } from './ActionIcons';
import AppModal from './AppModal';
import AppToastContainer, { AppToastItem } from './AppToastContainer';

const URL_BASE = (process.env.REACT_APP_API_URL || '').replace(/\/$/, '');

type Cycle = {
  _id: string;
  name: string;
  profileGenClientId: string;
  clientName: string;
  profileModel?: string;
  slug: string;
  status: 'draft' | 'active' | 'closed';
  createdAt?: string;
};

type KeyUser = {
  _id: string;
  keyUserId: string;
  keyUserName: string;
  isActive: boolean;
  responsibleForUsers: string[];
  cycleId: string;
};

type ImportResult = {
  keyUserId: string;
  keyUserName: string;
  action: 'created' | 'updated';
  tempPassword: string;
  count: number;
};

type MappingUser = {
  user: string;
  usuario_name: string;
};

const STATUS_LABELS: Record<string, string> = {
  draft: 'Rascunho',
  active: 'Ativo',
  closed: 'Encerrado',
};
const STATUS_COLORS: Record<string, string> = {
  draft: 'secondary',
  active: 'primary',
  closed: 'success',
};

const ADSCycle: React.FC = () => {
  const { cycleId } = useParams<{ cycleId: string }>();
  const navigate = useNavigate();

  const [cycle, setCycle] = useState<Cycle | null>(null);
  const [keyUsers, setKeyUsers] = useState<KeyUser[]>([]);
  const [loading, setLoading] = useState(true);
  const [kuLoading, setKuLoading] = useState(false);
  const [toasts, setToasts] = useState<AppToastItem[]>([]);

  // Key user modal
  const [showKuModal, setShowKuModal] = useState(false);
  const [editKu, setEditKu] = useState<KeyUser | null>(null);
  const [kuForm, setKuForm] = useState({ keyUserId: '', keyUserName: '', password: '', responsibleForUsers: [] as string[] });
  const [kuSubmitting, setKuSubmitting] = useState(false);

  // User picker
  const [availableUsers, setAvailableUsers] = useState<MappingUser[]>([]);
  const [loadingUsers, setLoadingUsers] = useState(false);
  const [userSearch, setUserSearch] = useState('');

  // Delete confirm
  const [deleteKuId, setDeleteKuId] = useState<string | null>(null);

  // Import
  const importRef = useRef<HTMLInputElement>(null);
  const [importResults, setImportResults] = useState<ImportResult[] | null>(null);
  const [importing, setImporting] = useState(false);

  // Copy link
  const [linkCopied, setLinkCopied] = useState(false);

  const addToast = (message: string, variant: AppToastItem['variant'] = 'success') => {
    const id = Date.now().toString();
    setToasts((prev) => [...prev, { id, message, variant }]);
  };

  const fetchCycle = useCallback(async () => {
    try {
      const res = await axios.get(`${URL_BASE}/api/ads/cycles/${cycleId}`, { withCredentials: true });
      setCycle(res.data);
    } catch (e: any) {
      addToast(`Erro ao carregar ciclo: ${e?.response?.data?.message || String(e)}`, 'danger');
    }
  }, [cycleId]);

  const fetchKeyUsers = useCallback(async () => {
    setKuLoading(true);
    try {
      const res = await axios.get(`${URL_BASE}/api/ads/cycles/${cycleId}/key-users`, { withCredentials: true });
      setKeyUsers(Array.isArray(res.data?.items) ? res.data.items : []);
    } catch (e: any) {
      addToast(`Erro ao carregar key users: ${e?.response?.data?.message || String(e)}`, 'danger');
    } finally {
      setKuLoading(false);
    }
  }, [cycleId]);

  useEffect(() => {
    (async () => {
      setLoading(true);
      await Promise.all([fetchCycle(), fetchKeyUsers()]);
      setLoading(false);
    })();
  }, [fetchCycle, fetchKeyUsers]);

  const fetchAvailableUsers = useCallback(async () => {
    setLoadingUsers(true);
    try {
      const res = await axios.get(`${URL_BASE}/api/ads/cycles/${cycleId}/mapping-users`, { withCredentials: true });
      setAvailableUsers(Array.isArray(res.data?.items) ? res.data.items : []);
    } catch {
      setAvailableUsers([]);
    } finally {
      setLoadingUsers(false);
    }
  }, [cycleId]);

  // ── Key user CRUD ───────────────────────────────────────

  const openCreateKu = () => {
    setEditKu(null);
    setKuForm({ keyUserId: '', keyUserName: '', password: '', responsibleForUsers: [] });
    setUserSearch('');
    setShowKuModal(true);
    fetchAvailableUsers();
  };

  const openEditKu = (ku: KeyUser) => {
    setEditKu(ku);
    setKuForm({
      keyUserId: ku.keyUserId,
      keyUserName: ku.keyUserName,
      password: '',
      responsibleForUsers: ku.responsibleForUsers || [],
    });
    setUserSearch('');
    setShowKuModal(true);
    fetchAvailableUsers();
  };

  const handleKuSubmit = async () => {
    if (!kuForm.keyUserId.trim() || !kuForm.keyUserName.trim()) {
      addToast('ID e nome são obrigatórios.', 'warning');
      return;
    }
    if (!editKu && !kuForm.password.trim()) {
      addToast('Senha é obrigatória para novo key user.', 'warning');
      return;
    }
    setKuSubmitting(true);
    try {
      const responsible = kuForm.responsibleForUsers;

      if (editKu) {
        const payload: any = { keyUserName: kuForm.keyUserName, responsibleForUsers: responsible };
        if (kuForm.password.trim()) payload.password = kuForm.password;
        await axios.put(
          `${URL_BASE}/api/ads/cycles/${cycleId}/key-users/${editKu._id}`,
          payload,
          { withCredentials: true }
        );
        addToast('Key user atualizado.');
      } else {
        await axios.post(
          `${URL_BASE}/api/ads/cycles/${cycleId}/key-users`,
          {
            keyUserId: kuForm.keyUserId,
            keyUserName: kuForm.keyUserName,
            password: kuForm.password,
            responsibleForUsers: responsible,
          },
          { withCredentials: true }
        );
        addToast('Key user criado.');
      }
      setShowKuModal(false);
      fetchKeyUsers();
    } catch (e: any) {
      addToast(`Erro: ${e?.response?.data?.message || String(e)}`, 'danger');
    } finally {
      setKuSubmitting(false);
    }
  };

  const handleDeleteKu = async () => {
    if (!deleteKuId) return;
    try {
      await axios.delete(`${URL_BASE}/api/ads/cycles/${cycleId}/key-users/${deleteKuId}`, { withCredentials: true });
      addToast('Key user excluído.');
      setDeleteKuId(null);
      fetchKeyUsers();
    } catch (e: any) {
      addToast(`Erro: ${e?.response?.data?.message || String(e)}`, 'danger');
    }
  };

  const handleToggleActive = async (ku: KeyUser) => {
    try {
      await axios.put(
        `${URL_BASE}/api/ads/cycles/${cycleId}/key-users/${ku._id}`,
        { isActive: !ku.isActive },
        { withCredentials: true }
      );
      addToast(`Key user ${ku.isActive ? 'desativado' : 'ativado'}.`);
      fetchKeyUsers();
    } catch (e: any) {
      addToast(`Erro: ${e?.response?.data?.message || String(e)}`, 'danger');
    }
  };

  // ── Status change ───────────────────────────────────────────────────────────

  const handleDownloadCredentials = (results: ImportResult[]) => {
    const ws = XLSX.utils.json_to_sheet(
      results.map((r) => ({
        'ID / Login': r.keyUserId,
        Nome: r.keyUserName,
        Senha: r.tempPassword,
        'Usuários SAP': r.count,
        Ação: r.action === 'created' ? 'Criado' : 'Atualizado',
      }))
    );
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, 'Key Users');
    XLSX.writeFile(wb, `ads-credenciais-${cycle?.profileGenClientId || cycleId}.xlsx`);
  };

  const handleStatusChange = async (status: Cycle['status']) => {
    try {
      await axios.put(`${URL_BASE}/api/ads/cycles/${cycleId}`, { status }, { withCredentials: true });
      addToast(`Status alterado para "${STATUS_LABELS[status]}".`);
      fetchCycle();
    } catch (e: any) {
      addToast(`Erro: ${e?.response?.data?.message || String(e)}`, 'danger');
    }
  };

  // ── Export XLS ──────────────────────────────────────────────────────────────

  const handleExport = async () => {
    try {
      const res = await axios.get(`${URL_BASE}/api/ads/cycles/${cycleId}/export-users`, {
        withCredentials: true,
        responseType: 'blob',
      });
      const url = window.URL.createObjectURL(new Blob([res.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `ads-users-${cycle?.profileGenClientId || cycleId}.xlsx`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
    } catch (e: any) {
      addToast('Erro ao exportar planilha.', 'danger');
    }
  };

  // ── Import XLS ──────────────────────────────────────────────────────────────

  const handleImportClick = () => importRef.current?.click();

  const handleFileChange = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setImporting(true);
    setImportResults(null);
    try {
      const base64 = await new Promise<string>((resolve, reject) => {
        const reader = new FileReader();
        reader.onload = () => {
          const result = reader.result as string;
          // strip data URL prefix
          const b64 = result.includes(',') ? result.split(',')[1] : result;
          resolve(b64);
        };
        reader.onerror = reject;
        reader.readAsDataURL(file);
      });

      const res = await axios.post(
        `${URL_BASE}/api/ads/cycles/${cycleId}/import-key-users`,
        { fileBase64: base64 },
        { withCredentials: true }
      );
      setImportResults(res.data?.results || []);
      addToast(`Importação concluída: ${res.data?.results?.length || 0} key users processados.`);
      fetchKeyUsers();
    } catch (e: any) {
      addToast(`Erro na importação: ${e?.response?.data?.message || String(e)}`, 'danger');
    } finally {
      setImporting(false);
      if (importRef.current) importRef.current.value = '';
    }
  };

  // ── Copy portal link ────────────────────────────────────────────────────────

  const portalUrl = cycle ? `${window.location.origin}/ADS/${cycle.slug}` : '';

  const copyLink = () => {
    if (!portalUrl) return;
    navigator.clipboard.writeText(portalUrl).then(() => {
      setLinkCopied(true);
      setTimeout(() => setLinkCopied(false), 2000);
    });
  };

  // ── Table columns ───────────────────────────────────────────────────────────

  const kuColumns: DataTableColumn<KeyUser>[] = [
    {
      key: 'isActive',
      label: '',
      width: 60,
      render: (v) => (
        <span className={`badge bg-${v ? 'success' : 'secondary'}`} style={{ fontSize: '0.72rem' }}>
          {v ? 'Ativo' : 'Inativo'}
        </span>
      ),
    },
    { key: 'keyUserId', label: 'ID / Login', sortable: true },
    { key: 'keyUserName', label: 'Nome', sortable: true },
    {
      key: 'responsibleForUsers',
      label: 'Usuários SAP',
      render: (v: string[]) => (
        <span className="text-muted" style={{ fontSize: '0.82rem' }}>
          {Array.isArray(v) ? `${v.length} usuário(s)` : '—'}
        </span>
      ),
    },
    {
      key: '_id',
      label: 'Ações',
      render: (_, row) => (
        <ActionMenu
          actions={[
            {
              key: 'edit',
              label: 'Editar',
              icon: Icons.Pencil,
              onClick: () => openEditKu(row),
              variant: 'secondary',
            },
            {
              key: 'toggle',
              label: row.isActive ? 'Desativar' : 'Ativar',
              icon: row.isActive ? Icons.Lock : Icons.Unlock,
              onClick: () => handleToggleActive(row),
              variant: row.isActive ? 'warning' : 'success',
            },
            {
              key: 'delete',
              label: 'Excluir',
              icon: Icons.Trash,
              onClick: () => setDeleteKuId(row._id),
              variant: 'danger',
            },
          ]}
        />
      ),
    },
  ];

  if (loading) {
    return (
      <div className="container py-5" style={{ marginTop: 80 }}>
        <div className="spinner-border spinner-border-sm me-2" role="status" />
        <span className="text-muted">Carregando ciclo…</span>
      </div>
    );
  }

  if (!cycle) {
    return (
      <div className="container py-5" style={{ marginTop: 80 }}>
        <p className="text-danger">Ciclo não encontrado.</p>
        <button className="btn btn-outline-secondary" onClick={() => navigate('/ads')}>
          Voltar para ADS
        </button>
      </div>
    );
  }

  return (
    <div className="container py-5" style={{ marginTop: 80 }}>
      <AppToastContainer toasts={toasts} onDismiss={(id: string) => setToasts((t) => t.filter((x) => x.id !== id))} />

      {/* Header */}
      <div className="d-flex justify-content-between align-items-start mb-3">
        <div>
          <h1 className="mb-0">{cycle.name}</h1>
          <p className="lead mb-0">
            Cliente: <strong>{cycle.clientName}</strong> &nbsp;|&nbsp; ID: <code>{cycle.profileGenClientId}</code>
            &nbsp;|&nbsp; Modelo de perfis: <strong>{cycle.profileModel || '—'}</strong>
            &nbsp;|&nbsp;
            <span className={`badge bg-${STATUS_COLORS[cycle.status] || 'secondary'}`}>
              {STATUS_LABELS[cycle.status] || cycle.status}
            </span>
          </p>
        </div>
        <div className="d-flex gap-2 flex-wrap align-items-center">
          <button className="btn btn-outline-secondary" onClick={() => navigate('/ads')}>← Voltar para ADS</button>
          {cycle.status !== 'active' && (
              <button className="btn btn-success btn-sm" onClick={() => handleStatusChange('active')}>
                Ativar ciclo
              </button>
            )}
            {cycle.status !== 'closed' && (
              <button className="btn btn-outline-warning btn-sm" onClick={() => handleStatusChange('closed')}>
                Encerrar ciclo
              </button>
            )}
            {cycle.status !== 'draft' && (
              <button className="btn btn-outline-secondary btn-sm" onClick={() => handleStatusChange('draft')}>
                Voltar a Rascunho
              </button>
            )}
        </div>
      </div>

      {/* Hero card */}
      <div className="adsc-hero">
        {/* Portal link */}
        <div>
          <label className="form-label fw-semibold mb-1" style={{ fontSize: '0.85rem' }}>
            Link do portal para key users
          </label>
          <div className="d-flex align-items-center gap-2">
            <div className="adsc-link-box flex-grow-1">{portalUrl}</div>
            <button className="btn btn-outline-primary btn-sm" onClick={copyLink} title="Copiar link">
              {linkCopied ? Icons.Check : Icons.Copy}
              <span className="ms-1">{linkCopied ? 'Copiado!' : 'Copiar'}</span>
            </button>
            <a
              className="btn btn-outline-secondary btn-sm"
              href={portalUrl}
              target="_blank"
              rel="noopener noreferrer"
              title="Abrir portal"
            >
              {Icons.Eye}
            </a>
          </div>
        </div>
      </div>

      {/* Export / Import section */}
      <div className="mb-4">
        <h5 className="mb-1">Planilha de Key Users</h5>
        <p className="text-muted mb-3" style={{ fontSize: '0.85rem' }}>
          Baixe a lista de usuários SAP do mapeamento de referência, preencha os campos{' '}
          <code>key_user_name</code> e <code>key_user_id</code> para cada usuário e suba a planilha
          para criar/atualizar os key users automaticamente.
        </p>
        <div className="d-flex gap-2 flex-wrap">
          <button className="btn btn-outline-primary btn-sm" onClick={handleExport}>
            {Icons.Download}
            <span className="ms-1">Baixar planilha de usuários (.xlsx)</span>
          </button>
          <button className="btn btn-outline-success btn-sm" onClick={handleImportClick} disabled={importing}>
            {importing ? (
              <>
                <span className="spinner-border spinner-border-sm me-1" role="status" />
                Importando…
              </>
            ) : (
              <>
                {Icons.Upload}
                <span className="ms-1">Importar planilha preenchida (.xlsx)</span>
              </>
            )}
          </button>
          <input ref={importRef} type="file" accept=".xlsx,.xls" className="d-none" onChange={handleFileChange} />
        </div>

        {/* Import results */}
        {importResults && importResults.length > 0 && (
          <div className="mt-3">
            <div className="fw-semibold mb-2" style={{ fontSize: '0.88rem' }}>
              Resultado da importação ({importResults.length} key users):
            </div>
            <div className="adsc-import-result">
              <table className="table table-sm table-bordered mb-0" style={{ fontSize: '0.82rem' }}>
                <thead className="table-light">
                  <tr>
                    <th>ID</th>
                    <th>Nome</th>
                    <th>Ação</th>
                    <th>Usuários</th>
                    <th>Senha</th>
                  </tr>
                </thead>
                <tbody>
                  {importResults.map((r) => (
                    <tr key={r.keyUserId}>
                      <td>{r.keyUserId}</td>
                      <td>{r.keyUserName}</td>
                      <td>
                        <span className={`badge bg-${r.action === 'created' ? 'primary' : 'secondary'}`} style={{ fontSize: '0.72rem' }}>
                          {r.action === 'created' ? 'Criado' : 'Atualizado'}
                        </span>
                      </td>
                      <td>{r.count}</td>
                      <td><span className="adsc-temp-pw">{r.tempPassword}</span></td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            <div className="d-flex align-items-center gap-3 mt-2">
              <button
                className="btn btn-success btn-sm"
                onClick={() => handleDownloadCredentials(importResults)}
              >
                {Icons.Download}
                <span className="ms-1">Baixar credenciais (.xlsx)</span>
              </button>
              <small className="text-warning">
                ⚠ As senhas não ficam armazenadas em texto. Baixe o arquivo antes de sair desta página.
              </small>
            </div>
          </div>
        )}
      </div>

      {/* Key users table */}
      <div className="mb-4">
        <div className="d-flex align-items-center justify-content-between mb-3">
          <h5 className="mb-0">Key Users ({keyUsers.length})</h5>
          <button className="btn btn-secondary gp-uniform-btn" onClick={openCreateKu}>
            + Novo Key User
          </button>
        </div>
        <DataTable
          columns={kuColumns}
          data={keyUsers}
          keyExtractor={(r) => r._id}
          loading={kuLoading}
          emptyMessage="Nenhum key user cadastrado. Importe a planilha ou crie manualmente."
          hover
          striped
        />
      </div>

      {/* Key user modal */}
      <AppModal
          show={showKuModal}
          title={editKu ? `Editar Key User – ${editKu.keyUserId}` : 'Novo Key User'}
          onClose={() => setShowKuModal(false)}
          footer={
            <>
              <button className="btn btn-secondary btn-sm" onClick={() => setShowKuModal(false)} disabled={kuSubmitting}>
                Cancelar
              </button>
              <button className="btn btn-primary btn-sm" onClick={handleKuSubmit} disabled={kuSubmitting}>
                {kuSubmitting ? 'Salvando…' : editKu ? 'Salvar' : 'Criar'}
              </button>
            </>
          }
        >
          <div className="mb-3">
            <label className="form-label fw-semibold">ID / Login *</label>
            <input
              className="form-control form-control-sm"
              placeholder="ex.: JSILVA"
              value={kuForm.keyUserId}
              disabled={!!editKu}
              onChange={(e) => setKuForm((p) => ({ ...p, keyUserId: e.target.value.trim() }))}
            />
          </div>
          <div className="mb-3">
            <label className="form-label fw-semibold">Nome completo *</label>
            <input
              className="form-control form-control-sm"
              placeholder="ex.: João Silva"
              value={kuForm.keyUserName}
              onChange={(e) => setKuForm((p) => ({ ...p, keyUserName: e.target.value }))}
            />
          </div>
          <div className="mb-3">
            <label className="form-label fw-semibold">{editKu ? 'Nova senha (deixe em branco para manter)' : 'Senha *'}</label>
            <input
              className="form-control form-control-sm"
              type="password"
              placeholder="Senha de acesso ao portal"
              value={kuForm.password}
              onChange={(e) => setKuForm((p) => ({ ...p, password: e.target.value }))}
            />
          </div>
          <div className="mb-1">
            <label className="form-label fw-semibold">Usuários SAP sob responsabilidade</label>

            {/* Search bar */}
            <div className="position-relative mb-2">
              <input
                className="form-control form-control-sm"
                placeholder="Digite para pesquisar usuários do mapeamento…"
                value={userSearch}
                onChange={(e) => setUserSearch(e.target.value)}
                autoComplete="off"
              />

              {/* Dropdown results */}
              {userSearch.trim() !== '' && (
                <div className="adsc-user-dropdown border rounded shadow-sm">
                  {loadingUsers ? (
                    <div className="adsc-user-dropdown-item text-muted">
                      <span className="spinner-border spinner-border-sm me-1" role="status" />
                      Carregando…
                    </div>
                  ) : (() => {
                    const q = userSearch.trim().toLowerCase();
                    const filtered = availableUsers.filter(
                      (u) =>
                        (u.user.toLowerCase().includes(q) || u.usuario_name.toLowerCase().includes(q)) &&
                        !kuForm.responsibleForUsers.includes(u.user)
                    );
                    return filtered.length === 0 ? (
                      <div className="adsc-user-dropdown-item text-muted">Nenhum usuário encontrado.</div>
                    ) : (
                      filtered.slice(0, 100).map((u) => (
                        <button
                          key={u.user}
                          type="button"
                          className="adsc-user-dropdown-item adsc-user-dropdown-action"
                          onClick={() => {
                            setKuForm((p) => ({ ...p, responsibleForUsers: [...p.responsibleForUsers, u.user] }));
                            setUserSearch('');
                          }}
                        >
                          <strong>{u.user}</strong> — {u.usuario_name}
                        </button>
                      ))
                    );
                  })()}
                </div>
              )}
            </div>

            {/* Assigned users — standard DataTable */}
            <DataTable
              columns={[
                {
                  key: 'user',
                  label: 'ID do Usuário',
                  sortable: true,
                  render: (v: string) => <code>{v}</code>,
                },
                {
                  key: '_usuario_name',
                  label: 'Nome do Usuário',
                  sortable: false,
                  render: (_v: any, row: { user: string }) => {
                    const userInfo = availableUsers.find((u) => u.user === row.user);
                    return userInfo?.usuario_name || '—';
                  },
                },
                {
                  key: '_remove',
                  label: 'Ação',
                  sortable: false,
                  minWidth: 48,
                  render: (_v: any, row: { user: string }) => (
                    <button
                      type="button"
                      className="btn btn-link btn-sm text-danger p-0"
                      title="Remover"
                      onClick={() =>
                        setKuForm((p) => ({
                          ...p,
                          responsibleForUsers: p.responsibleForUsers.filter((x) => x !== row.user),
                        }))
                      }
                    >
                      {Icons.Trash}
                    </button>
                  ),
                },
              ]}
              data={kuForm.responsibleForUsers.map((u) => ({ user: u }))}
              keyExtractor={(r: { user: string }) => r.user}
              clientSidePagination
              pagination
              pageSize={10}
              pageSizeOptions={[10, 25, 50]}
              hover
              striped
              size="sm"
              showColumnMenu={false}
              emptyMessage="Nenhum usuário atribuído. Pesquise acima para adicionar."
            />
          </div>
        </AppModal>

      {/* Delete confirm */}
      <AppModal
          show={!!deleteKuId}
          title="Confirmar exclusão"
          onClose={() => setDeleteKuId(null)}
          footer={
            <>
              <button className="btn btn-secondary btn-sm" onClick={() => setDeleteKuId(null)}>
                Cancelar
              </button>
              <button className="btn btn-danger btn-sm" onClick={handleDeleteKu}>
                Excluir
              </button>
            </>
          }
        >
          <p>Confirma a exclusão deste key user?</p>
        </AppModal>
    </div>
  );
};

export default ADSCycle;
